/**
 * Quote Service
 * Handles creating and sending quotes to clients
 */

import { toast } from 'sonner';
import { apiClient as api } from '@/lib/api';
import { buildItineraryProposalEmailHtml } from '@/utils/emailTemplates';
/**
 * Creates a new quote
 * @param {Object} quoteData - Complete quote data
 * @returns {Promise<Object>} Promise resolving to created quote object
 */
export const createQuote = async (quoteData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  // Validate quote data
  if (!quoteData.client) {
    throw new Error('Client information is required');
  }

  if (!quoteData.aircraft) {
    throw new Error('Aircraft selection is required');
  }

  if (!quoteData.flights || quoteData.flights.length === 0) {
    throw new Error('Flight details are required');
  }

  if (!quoteData.settings?.bidTitle) {
    throw new Error('Quote title is required');
  }

  // Create quote object
  const quote = {
    id: Date.now(),
    ...quoteData,
    status: 'draft',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return quote;
};

/**
 * Sends a quote to the client
 * @param {Object} quoteData - Quote data to send
 * @param {Object} queryClient - React Query client instance (from useQueryClient hook)
 * @returns {Promise<Object>} Promise resolving to sent quote with confirmation
 */
export const sendQuoteToClient = async (quoteData, queryClient, organization = null) => {
  
  const recipientEmail = quoteData.client?.clientData?.email || quoteData.client?.email;
  if (recipientEmail === "quotes@trustjet.ai") {
    toast.warning('Email "From" cannot be the same as "To"', { position: 'bottom-right' });
    return;
  }

  // Transform quote data to match API requirements
  const apiPayload = {
    client_id: quoteData.client?.clientData?.client_id,

    departure_date: quoteData.departure_date || null,
    return_date: quoteData.return_date || null,
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    quote_number: quoteData.settings?.quoteNumber || `TR-${Date.now()}`,

    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    is_multi_leg: quoteData.flightType === "multi-leg",
    is_round_trip: quoteData.flightType === "round-trip",

    // overrides
    broker_org_id: quoteData.broker_org_id,
    created_by: quoteData.created_by,
    status: quoteData.status,
    bid_items: quoteData.bid_items || [],
    total_due: quoteData.total_due,
    broker_margin: quoteData.broker_margin,
    platform_fee: quoteData.platform_fee,
    note_to_client: quoteData.settings.notes || "",
    federal_excise_tax: quoteData.federal_excise_tax,
    operator_cost: quoteData.operator_cost,
    taxable_amount: quoteData.taxable_amount,
    segment_tax: quoteData.segment_tax,
    card_fee: quoteData.card_fee
  };

  // Call API to create the quote
  const sentQuote = await api.createQuote(apiPayload);

  const quoteid = sentQuote?.quote_id;

  const requestToken = await api.createCheckoutToken({
      "quote_id": quoteid,
      "amount": quoteData.total_due,
      "currency": "USD",
      "payment_method": "CREDIT_CARD",
      "description": "Charter flight quote payment",
      "metadata": {
          "customer_name": "John Doe",
          "customer_email": "john@example.com"
      }
  });

  const quoteLink = `${window.location.origin}/quotepreview?quote=${quoteid}&token=${requestToken?.token}&org=${quoteData.broker_org_id}`;

  const organizationEmail = organization?.billing_email;

  // Build itinerary proposal email HTML
  const finalEmailMessage = await buildItineraryProposalEmailHtml({
    quote: sentQuote,
    quoteLink,
    passengers: quoteData.passengers || [],
    crew: quoteData.crew || null,
    services: quoteData.services || [],
    organization,
  });
 
  // Send message/email to client with the quote
  const messagePayload = {
    From: "quotes@trustjet.ai",
    To: quoteData.client?.clientData?.email || quoteData.client?.email,
    Cc: organizationEmail,
    ReplyTo: organizationEmail,
    Subject: quoteData.settings.emailSubject || 'Itinerary Proposal for Review',
    HtmlBody: finalEmailMessage,
    Attachments: []
  };

  await api.sendMessage(messagePayload);

  return sentQuote;
};

/**
 * Saves a quote as draft
 * @param {Object} quoteData - Quote data to save
 * @returns {Promise<Object>} Promise resolving to saved draft
 */
export const saveQuoteDraft = async (quoteData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const draft = {
    id: Date.now(),
    ...quoteData,
    status: 'draft',
    savedAt: new Date().toISOString(),
  };

  return draft;
};
